import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import * as actions from '../actions/stories.action';
import { switchMap, map, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { StoriesService } from '../../services';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { Go } from '../../../store';
import { Store } from '@ngrx/store';
import { getStoriesPage, getStoriesSortOrder } from '../selectors';
import { getGeoLocationPosition } from 'src/app/geo-location/store';

@Injectable()
export class StoriesEffects {
  constructor(
    private actions$: Actions,
    private service: StoriesService,
    private router: Router,
    private sb: MatSnackBar,
    private store: Store
  ) {}

  LoadStory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadStory),
      switchMap(({ id }) =>
        this.service.getStory(id).pipe(
          map(story => actions.LoadStorySuccess({ story })),
          catchError(error => of(actions.LoadStoryFail({ error })))
        )
      )
    );
  });

  LoadStories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadStories),
      withLatestFrom(this.store.select(getStoriesSortOrder), this.store.select(getGeoLocationPosition)),
      switchMap(([{ skip, take, theme }, order, pos]) =>
        this.service.getStories(skip, take, theme, order, `${pos?.coords.latitude}`, `${pos?.coords.longitude}`).pipe(
          map(stories => actions.LoadStoriesSuccess({ stories })),
          catchError(error => of(actions.LoadStoriesFail({ error })))
        )
      )
    );
  });

  LoadMoreStories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadMoreStories),
      withLatestFrom(this.store.select(getStoriesPage), this.store.select(getStoriesSortOrder), this.store.select(getGeoLocationPosition)),
      switchMap(([{}, pagedata, order, pos]) =>
        this.service
          .getStories(
            (pagedata.currentPage + 1) * pagedata.pageSize,
            pagedata.pageSize,
            pagedata.filters['theme'],
            order,
            `${pos?.coords.latitude}`,
            `${pos?.coords.longitude}`
          )
          .pipe(
            map(stories => actions.LoadMoreStoriesSuccess({ stories })),
            catchError(error => of(actions.LoadStoriesFail({ error })))
          )
      )
    );
  });

  CreateStory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.CreateStory),
      switchMap(({ storyData }) =>
        this.service.createStory(storyData).pipe(
          map(story => actions.CreateStorySuccess({ story })),
          catchError(error => of(actions.CreateStoryFail({ error })))
        )
      )
    );
  });

  UpdateStory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.UpdateStory),
      switchMap(({ id, storyData }) =>
        this.service.updateStory(id, storyData).pipe(
          map(story => actions.UpdateStorySuccess({ story })),
          catchError(error => of(actions.UpdateStoryFail({ error })))
        )
      )
    );
  });

  DeleteStory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.DeleteStory),
      switchMap(({ id }) =>
        this.service.deleteStory(id).pipe(
          map(story => actions.DeleteStorySuccess({ story })),
          catchError(error => of(actions.DeleteStoryFail({ error })))
        )
      )
    );
  });

  updateStorySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.UpdateStorySuccess),
      tap(({ story }) => {
        this.sb.open('Verhaal geüpdatet!', '');
      }),
      map(({ story }) => Go({ path: story.url }))
    );
  });

  createStorySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.CreateStorySuccess),
      tap(({ story }) => {
        this.sb.open('Verhaal aangemaakt!', '');
      }),
      map(({ story }) => Go({ path: story.url }))
    );
  });

  deleteStorySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.DeleteStorySuccess),
      tap(({ story }) => {
        this.sb.open('Verhaal verwijderd!', '');
      }),
      map(({ story }) => Go({ path: ['/', 'initiatieven'] }))
    );
  });

  LikeStory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LikeStory),
      switchMap(({ id }) =>
        this.service.likeStory(id).pipe(
          map(story => actions.LikeStorySuccess({ story })),
          catchError(error => of(actions.LikeStoryFail({ error })))
        )
      )
    );
  });

  LoadByIdFail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadStoryFail),
      map(({ error }) => {
        return Go({ path: ['/' + error?.status ? error.status : '404'], extras: { skipLocationChange: true } });
      })
    );
  });
}
